import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { messageChannelWord, messageKindLabel } from '../status-labels.js'

/**
 * Telling one customer something. §6.17 `MSG-002`, `MSG-003`, `MSG-004`.
 *
 * Sits under the customer record rather than in the rail, because this is a thing somebody does
 * while looking at a person: their glasses are ready, their order is late. The outbox is the other
 * half and is where the queue and the failures live.
 *
 * **The shop's wording, never the counter's.** `MSG-002` makes every message a template, so there
 * is no free-text box here: what can be said is what the shop has written and turned on, and the
 * only thing this screen supplies is the values the template asks for.
 *
 * **The locale is the server's choice, not this screen's.** `LOC-003` reads the customer's own
 * language and `loadTemplate` falls back to English, so sending a locale from here would be a
 * second copy of that rule. The consequence is handled rather than ignored: a kind may have an
 * Urdu template and an English one whose bodies name different placeholders, and the server may
 * pick either, so the form asks for the **union** of what every active template for this kind and
 * channel needs. A value the chosen body does not name is ignored by `renderTemplate`; a value it
 * names and nobody supplied is `TEMPLATE_VALUE_MISSING` and a message that never goes.
 *
 * **`MSG-003`'s warning is on the screen, because the requirement asks for one in those words.**
 * A template that embeds prescription values can only be filled by somebody typing dioptres into
 * this form: nothing in the product reads a prescription into a message. So the warning says both
 * things, that it is health data and that the number is being retyped.
 */

/**
 * One template as the server sends it, shared with the owner's editor.
 *
 * `version` and `subject` are no use to this panel and are carried because `MessageTemplates.tsx`
 * reads the same endpoint and needs both: one wire row mapped once, rather than two shapes of the
 * same thing drifting apart. `ARC-007` is why the editor needs the version at all.
 */
export interface MessageTemplateOption {
  readonly id: string
  readonly kind: string
  readonly channel: string
  readonly locale: string
  readonly isActive: boolean
  readonly body: string
  readonly valuesRequired: readonly string[]
  readonly embedsRxData: boolean
  readonly version: number
  readonly subject?: string
}

export interface SendMessageProps {
  readonly customerId: string
  readonly branchId: string
  readonly loadTemplates: () => Promise<readonly MessageTemplateOption[]>
}

interface Sendable {
  readonly key: string
  readonly kind: string
  readonly channel: string
  readonly locales: readonly string[]
  readonly valuesRequired: readonly string[]
  readonly embedsRxData: boolean
  readonly body: string
}

/** One entry per kind and channel, whatever locales the shop has written for it. */
function sendables(templates: readonly MessageTemplateOption[]): readonly Sendable[] {
  const groups = new Map<string, MessageTemplateOption[]>()
  for (const template of templates) {
    if (!template.isActive) continue
    const key = `${template.kind}|${template.channel}`
    groups.set(key, [...(groups.get(key) ?? []), template])
  }
  return [...groups.entries()].map(([key, group]) => {
    const needed = new Set<string>()
    for (const template of group) for (const name of template.valuesRequired) needed.add(name)
    return {
      key,
      kind: group[0]?.kind ?? '',
      channel: group[0]?.channel ?? '',
      locales: group.map((template) => template.locale).sort(),
      valuesRequired: [...needed].sort(),
      embedsRxData: group.some((template) => template.embedsRxData),
      // The English one if there is one, because that is the fallback the server lands on and the
      // preview should be a body somebody here can read.
      body: (group.find((template) => template.locale === 'en') ?? group[0])?.body ?? '',
    }
  })
}

export function SendMessage({
  customerId,
  branchId,
  loadTemplates,
}: SendMessageProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [templates, setTemplates] = useState<readonly MessageTemplateOption[] | null>(null)
  const [chosen, setChosen] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  /** The form's own complaint (nothing chosen, a value missing). Never a failed load. */
  const [error, setError] = useState<string | null>(null)
  /**
   * A failed read. It used to become `setTemplates([])`, which rendered 'the shop has not written
   * any messages' beside the error: a false empty. Templates stay null until a read succeeds.
   */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [writeId, setWriteId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    loadTemplates()
      .then((loaded) => {
        if (cancelled) return
        setTemplates(loaded)
        setLoadError(null)
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        setLoadError(caught instanceof Error ? caught.message : t('sendMessage.loadFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [loadTemplates, t, attempt])

  const options = useMemo(() => sendables(templates ?? []), [templates])
  const option = options.find((candidate) => candidate.key === chosen)

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setChosen('')
    setValues({})
  })

  const send = useCallback(async () => {
    if (option === undefined) {
      setError(t('sendMessage.chooseOne'))
      return
    }
    const missing = option.valuesRequired.filter((name) => (values[name] ?? '').trim() === '')
    if (missing.length > 0) {
      // `renderTemplate` refuses a missing value, because "Your order {{order_number}} is ready"
      // is worse than no message. Said here so the refusal does not arrive as a queue entry.
      setError(t('sendMessage.valuesMissing', { names: missing.join(', ') }))
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/messages',
      label: t('sendMessage.queueLabel'),
      body: {
        kind: option.kind,
        channel: option.channel,
        customerId,
        branchId,
        values: Object.fromEntries(
          option.valuesRequired.map((name) => [name, (values[name] ?? '').trim()]),
        ),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [branchId, customerId, enqueue, option, t, values])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.message'))

  return (
    <section className="lm-send-message" aria-labelledby="send-message-heading">
      <h3 id="send-message-heading" className="lm-send-message__heading">
        {t('sendMessage.heading')}
      </h3>

      {/*
        Two empties that are not first-run and filtered: nothing written, and written but all
        turned off. The second used to say the first, which sent an owner to write what they had.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  setTemplates(null)
                  setAttempt((n) => n + 1)
                },
              }
            : templates === null
              ? { kind: 'loading' }
              : options.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="lines"
        rows={2}
        what={t('sendMessage.what')}
        emptyMessage={
          templates !== null && templates.length > 0
            ? t('sendMessage.noneActive')
            : t('sendMessage.noTemplates')
        }
      >
        <>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('sendMessage.whatToSay')}</span>
            <select
              className="lm-input"
              value={chosen}
              onChange={(e) => {
                const value = e.currentTarget.value
                setChosen(value)
                setValues({})
                setError(null)
              }}
            >
              <option value="">{t('sendMessage.chooseOne')}</option>
              {options.map((candidate) => (
                <option key={candidate.key} value={candidate.key}>
                  {t('sendMessage.option', {
                    kind: messageKindLabel(t, candidate.kind),
                    channel: messageChannelWord(t, candidate.channel),
                  })}
                </option>
              ))}
            </select>
          </label>

          {option === undefined ? null : (
            <>
              {/* The words that will go out, as the shop wrote them, with the braces still in:
                  what fills them is the form below, and a preview with the values already dropped
                  in would hide which parts a person is responsible for. */}
              <p className="lm-send-message__preview">{option.body}</p>

              {option.locales.length > 1 ? (
                <p className="lm-hint">
                  {t('sendMessage.locales', { locales: option.locales.join(', ') })}
                </p>
              ) : null}

              {option.embedsRxData ? (
                // `MSG-003`: "Templates that would embed Rx data require explicit tenant opt-in
                // with a warning." The shop has opted in or the server would not have let the
                // template be active, so this is the warning half.
                <p className="lm-send-message__rx" role="alert">
                  {t('sendMessage.rxWarning')}
                </p>
              ) : null}

              {option.valuesRequired.map((name) => (
                <label key={name} className="lm-field lm-field--inline">
                  {/* The placeholder's own name, untranslated, because it is the template's field
                      and the shop wrote the template. */}
                  <span className="lm-field__label">{name}</span>
                  <input
                    className="lm-input"
                    autoComplete="off"
                    value={values[name] ?? ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value
                      setValues((previous) => ({ ...previous, [name]: value }))
                    }}
                  />
                </label>
              ))}

              <button
                type="button"
                className="lm-button lm-button--primary"
                onClick={() => void send()}
              >
                {t('sendMessage.send')}
              </button>
            </>
          )}
        </>
      </DataSurface>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}
    </section>
  )
}
